import type { MarkerFormat } from "./schemas.js";

export type Tier = "anon" | "free" | "docs" | "api" | "pro" | "ultra";

const FREE_MODES: readonly MarkerFormat[] = ["xxxxx", "blackbar"] as const;
// Phase 3.4 (Pre-Creem Roadmap, 2026-06-26): `redacted` mode removed entirely
// (overlapped conceptually with `custom`; Phase 3.3 toggle activation fought
// with the picker). Per-category `custom` (rendered as "Custom [LABEL]" in the
// picker) is the customer-visible Pro / Ultra perk now — picker selection IS
// the activation, no separate toggle.
//
// Phase 4 (2026-06-27): tier names reshaped to Free / Docs / API / Pro / Ultra.
// Docs and API are persona tiers (Lawyer/Compliance, Dev) that get the
// "Three-Tier Model" baseline plus `default` (category-numbered `[PERSON_1]`-style
// output). Pro is now the combo tier (formerly `proplus`) — it adds `custom` on
// top, same as Ultra. Single source of truth: gating below flows into
// anonymize.ts (request validation) and users.me.ts (persisted preference
// PATCH) — no route-level allowlist edits needed.
const BASE_PAID_MODES: readonly MarkerFormat[] = ["xxxxx", "blackbar", "default"] as const;
// `pseudonym` (T2, 2026-09-08): replaces PERSON/ADDRESS spans with a
// plausible-but-fictional name instead of a bracketed marker. Pro/Ultra only,
// same rank as `custom` — not offered to Docs/API's persona tiers.
const PRO_MODES: readonly MarkerFormat[] = [...BASE_PAID_MODES, "custom", "pseudonym"] as const;

export function getAllowedMarkerFormats(tier: Tier): readonly MarkerFormat[] {
  if (tier === "anon" || tier === "free") return FREE_MODES;
  if (tier === "docs" || tier === "api") return BASE_PAID_MODES;
  return PRO_MODES;
}

/**
 * Inverse of `getAllowedMarkerFormats`: returns the lowest tier that can
 * select the given format. Used by the picker to render a "(Pro+)" / "(Docs+)"
 * suffix on locked options.
 */
export function getMinTierForMarkerFormat(format: MarkerFormat): Tier {
  if (format === "xxxxx" || format === "blackbar") return "anon";
  if (format === "default") return "docs";
  if (format === "custom" || format === "pseudonym") return "pro";
  // Exhaustive - TypeScript will narrow this if MarkerFormat gains a variant.
  const _exhaustive: never = format;
  return _exhaustive;
}

export function getDefaultMarkerFormat(tier: Tier): MarkerFormat {
  if (tier === "anon" || tier === "free") return "xxxxx";
  return "default";
}
